/**
 * RichTextView — renders a stored prose field for reading.
 *
 * Two shapes of prose live in Nimbus and both have to render correctly, because no field
 * has ever been migrated: anything written since a register moved to the Quill editor is
 * HTML, and anything written before that (or by an API client) is plain text whose only
 * structure is its newlines. The Initiative Tracker has both — INIT-8A961C7E's
 * description is five plain-text paragraphs, while descriptions saved from now on are
 * Quill HTML.
 *
 * Getting the branch wrong is visible either way: plain text pushed through
 * `dangerouslySetInnerHTML` loses every newline and reads as one run-on block, and HTML
 * rendered as text shows its tags. So the shape is detected and each branch uses the
 * matching mechanism — only the HTML branch injects, plain text stays React children.
 *
 * The HTML branch keeps Quill's own `ql-editor` class, because Quill's list markers,
 * indent levels and line spacing are CSS scoped to that class: without it a bulleted list
 * loses its bullets and the read view drifts from what the author saw while typing. The
 * `np-rich-text` companion class (see quill-fixes.css) undoes the parts of `.ql-editor`
 * that only make sense for an editing surface.
 *
 * SECURITY: this component trusts its input, so the HTML branch is only safe for fields
 * whose API sanitises on write. Do not point it at a register whose Lambda has no
 * `sanitise_html()` — that is a stored-XSS path, not a rendering bug.
 *
 * An <img data-doc-image-id> (PolicyEditor's inline images) never carries a src in storage -
 * the bucket behind it is private, so nothing here can just point a browser at it. Once the
 * HTML is in the DOM, a post-render pass resolves whichever ids are actually present and fills
 * in a live URL. This is a no-op query for every field that never has one, which is almost all
 * of them, so it costs nothing to leave wired in generically rather than special-casing it
 * into just the document register's own read view.
 *
 * An <a data-doc-pdf-id> (PolicyEditor's embedded PDFs) follows the same never-store-a-url
 * rule and rides the same post-render pass - dangerouslySetInnerHTML mounts it as an empty,
 * href-less anchor, so hydratePdfCard (PolicyEditorPdf.ts) builds its visible card contents and
 * click-to-preview behaviour here, the read-view equivalent of the editor's own node view.
 */

import React, { useEffect, useRef } from 'react';
import { isRichTextEmpty } from './RichTextField';
import { documentsApi } from '../services/api';
import { hydratePdfCard, applyPdfCardState } from './PolicyEditorPdf';

/**
 * Detection rule: the value is HTML if it contains an opening or closing tag whose name is
 * one Quill actually emits. Deliberately not a bare `<…>` test — legacy plain text compares
 * numbers ("throughput < 5k > baseline") and would be misread as markup, whereas a plain-text
 * description that happens to contain a literal "<p>" or "<li>" does not occur.
 */
/**
 * Deliberately the SAME predicate as `_HAS_TAG_RE` in the backend sanitisers. That coupling is
 * the point: the backend returns a tagless value untouched (escaping it would double-escape on
 * screen), and this renderer sends a tagless value down the plain-text branch. If the two
 * disagreed, a value could be escaped by one and escaped again by the other, and the user
 * would read the literal characters `3 &lt; 4`.
 *
 * An enumerated tag list was tried first and was worse in both directions: it missed tags the
 * allowlist now permits (`code`, `sub`, `sup`), and it can never be kept in step with a
 * server-side allowlist that lives in four files. `<` followed by an optional `/` and a letter
 * is what "contains markup" actually means, and it is what the backend tests.
 *
 * The trade-off is accepted knowingly: legacy prose containing a literal `<p` or `<div`
 * mid-sentence would be read as markup. Prose comparing numbers is the realistic case
 * ("throughput < 5k"), and `<` followed by a space or digit is not matched.
 */
const HAS_TAG = /<\s*\/?\s*[A-Za-z]/;

export const looksLikeHtml = (value: string): boolean => HAS_TAG.test(value);

/**
 * Split plain text the way it was written: a blank line starts a new paragraph, a single
 * newline is a line break inside the current one. The legacy tracker descriptions rely on
 * both — numbered points are one-per-line inside a paragraph, sections are separated by a
 * blank line.
 */
const toParagraphs = (text: string): string[][] =>
  text
    .replace(/\r\n?/g, '\n')
    .trim()
    .split(/\n[ \t]*\n\s*/)
    .map((block) => block.split('\n'))
    .filter((lines) => lines.some((line) => line.trim()));

const escapeHtml = (text: string): string =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * Plain text → the HTML Quill would have produced for it, using the same paragraph rule as
 * the read view.
 *
 * Needed when a legacy plain-text field is opened in a Quill editor: Quill parses whatever
 * string it is given as HTML, so raw newlines would collapse to spaces and the author's
 * paragraphs would be silently flattened the first time the record was saved. Converting on
 * the way in makes that first save preserve the structure instead of destroying it.
 */
export const plainTextToHtml = (text: string): string =>
  toParagraphs(text)
    .map((lines) => `<p>${lines.map(escapeHtml).join('<br>')}</p>`)
    .join('');

export interface RichTextViewProps {
  /** The stored field, in either shape. */
  value: string | null | undefined;
  /**
   * What to show when there is nothing to render. A string gets the standard muted italic
   * line; pass a node when a page wants its own empty state.
   */
  empty?: React.ReactNode;
  /** Extra classes on the prose wrapper. */
  className?: string;
}

export const RichTextView: React.FC<RichTextViewProps> = ({
  value,
  empty = 'Nothing recorded.',
  className,
}) => {
  const text = value ?? '';
  const htmlContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = htmlContainerRef.current;
    if (!container) return;
    let cancelled = false;

    const images = container.querySelectorAll<HTMLImageElement>('img[data-doc-image-id]');
    if (images.length > 0) {
      const ids = Array.from(new Set(Array.from(images, (img) => img.getAttribute('data-doc-image-id')!)));
      documentsApi.resolveImageUrls(ids).then((urls) => {
        if (cancelled) return;
        images.forEach((img) => {
          const id = img.getAttribute('data-doc-image-id');
          const url = id ? urls[id] : null;
          if (url) {
            img.src = url;
          } else {
            img.classList.add('np-doc-image-broken');
          }
        });
      });
    }

    const pdfCards = container.querySelectorAll<HTMLAnchorElement>('a[data-doc-pdf-id]');
    if (pdfCards.length > 0) {
      pdfCards.forEach((card) => hydratePdfCard(card));
      const ids = Array.from(new Set(Array.from(pdfCards, (card) => card.getAttribute('data-doc-pdf-id')!)));
      // Same generic resolve-images endpoint images use above - not image-specific, see
      // resolve_image_urls's entityType check in the backend.
      documentsApi.resolveImageUrls(ids).then((urls) => {
        if (cancelled) return;
        pdfCards.forEach((card) => {
          const id = card.getAttribute('data-doc-pdf-id');
          applyPdfCardState(card, id ? urls[id] : null);
        });
      });
    }

    return () => { cancelled = true; };
  }, [text]);

  // An untouched Quill editor still saves "<p><br></p>", which is a truthy string and a
  // visually empty field — isRichTextEmpty is what tells those apart.
  if (isRichTextEmpty(text)) {
    return typeof empty === 'string' ? (
      <p className="np-sub fst-italic mb-0">{empty}</p>
    ) : (
      <>{empty}</>
    );
  }

  const classes = ['np-rich-text', 'ql-editor', className].filter(Boolean).join(' ');

  if (looksLikeHtml(text)) {
    return <div ref={htmlContainerRef} className={classes} dangerouslySetInnerHTML={{ __html: text }} />;
  }

  return (
    <div className={classes}>
      {toParagraphs(text).map((lines, blockIndex) => (
        <p key={blockIndex}>
          {lines.map((line, lineIndex) => (
            <React.Fragment key={lineIndex}>
              {lineIndex > 0 && <br />}
              {line}
            </React.Fragment>
          ))}
        </p>
      ))}
    </div>
  );
};

export default RichTextView;
